import React, { useState, useEffect, useCallback } from 'react';
import { Box, useMediaQuery, useTheme, CssBaseline } from '@mui/material';
import { Header } from '@layout/Header';
import { Sidebar } from '@layout/Sidebar';
import Footer from '@layout/Footer';
import { useAppTheme } from '@contexts/ThemeContext';
import { CommandBar } from '@/components/Dashboard';

interface LayoutProps {
    children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
    const { isDarkMode, toggleTheme } = useAppTheme();
    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Mobile and tablets (to match sidebar behavior)
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // Tablets
    const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // Desktop and up
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarExpanded, setSidebarExpanded] = useState(false);
    const [commandBarOpen, setCommandBarOpen] = useState(false);

    // Keyboard shortcut handler for CommandBar (Ctrl+K / Cmd+K)
    const handleKeyboardShortcut = useCallback((e: KeyboardEvent) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
            e.preventDefault();
            setCommandBarOpen(prev => !prev);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyboardShortcut);
        return () => document.removeEventListener('keydown', handleKeyboardShortcut);
    }, [handleKeyboardShortcut]);

    const collapsedWidth = 64;
    const expandedWidth = 200; // Further reduced from 240 to 200
    const currentSidebarWidth = sidebarExpanded ? expandedWidth : collapsedWidth;

    const handleMenuClick = () => {
        if (isMobile) {
            // Mobile and tablet behavior - toggle drawer
            setSidebarOpen(!sidebarOpen);
        } else {
            // Desktop behavior - toggle expanded state
            setSidebarExpanded(!sidebarExpanded);
        }
    };

    const handleSidebarClose = () => {
        setSidebarOpen(false);
    };

    const handleToggleExpanded = () => {
        setSidebarExpanded(!sidebarExpanded);
    };

    // Listen for custom close sidebar event
    useEffect(() => {
        const handleCloseSidebar = () => {
            if (isMobile) {
                setSidebarOpen(false);
            } else {
                setSidebarExpanded(false);
            }
        };

        window.addEventListener('closeSidebar', handleCloseSidebar);
        return () => window.removeEventListener('closeSidebar', handleCloseSidebar);
    }, [isMobile]);

    const handleMainContentClick = () => {
        // Close sidebar when clicking on main content (both mobile and desktop)
        if (isMobile && sidebarOpen) {
            setSidebarOpen(false);
        }
        // For desktop, close expanded sidebar when clicking on main content
        if (!isMobile && sidebarExpanded) {
            setSidebarExpanded(false);
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            width: '100%',
            margin: 0,
            padding: 0,
            position: 'relative',
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary
        }}>
            <CssBaseline />

            {/* Command Bar - Universal Search (Ctrl+K) */}
            <CommandBar
                open={commandBarOpen}
                onClose={() => setCommandBarOpen(false)}
            />

            {/* Header - Fixed at top */}
            <Header
                onMenuClick={handleMenuClick}
                isSidebarOpen={sidebarOpen}
                isSidebarExpanded={sidebarExpanded}
                darkMode={isDarkMode}
                onDarkModeChange={toggleTheme}
                onSearchClick={() => setCommandBarOpen(true)}
            />

            {/* Main Content Area with Sidebar */}
            <Box sx={{
                display: 'flex',
                flex: 1,
                position: 'relative',
                marginTop: {
                    xs: '56px',    // Mobile header height
                    sm: '64px',    // Tablet/Desktop header height
                },
                minHeight: {
                    xs: 'calc(100vh - 56px)', // Mobile
                    sm: 'calc(100vh - 64px)', // Tablet/Desktop
                },
                zIndex: 1, // Below sidebar and header
                width: '100%',
                overflow: 'hidden',
                padding: {
                    xs: '8px',     // Mobile padding
                    sm: '16px',    // Tablet padding
                    md: '24px',    // Desktop padding
                }
            }}>
                {/* Sidebar */}
                <Sidebar
                    open={sidebarOpen}
                    onClose={handleSidebarClose}
                    isExpanded={sidebarExpanded}
                    onToggleExpanded={handleToggleExpanded}
                    darkMode={isDarkMode}
                />

                {/* Main Content */}
                <Box
                    component="main"
                    onClick={handleMainContentClick}
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        width: '100%',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        margin: 0,
                        padding: 0,
                        pointerEvents: 'auto',
                        zIndex: 1,
                        position: 'relative',
                        boxSizing: 'border-box'
                    }}
                >
                    {children}
                </Box>
            </Box>

            {/* Footer - Only appears at bottom of content, above sidebar */}
            <Box sx={{
                zIndex: 1300, // Above sidebar (1200) but below header (1400)
                width: '100%',
                marginTop: 'auto' // Push footer to bottom of content
            }}>
                <Footer />
            </Box>
        </Box>
    );
};
